import React, { useState, useMemo, useCallback } from 'react';
import { Chart } from 'react-google-charts';
import styled from 'styled-components';
import * as Slider from '@radix-ui/react-slider';

const DownloadWrapper = styled.div`
  margin-block: 1rem;
  justify-content: flex-end;
  display: flex;
  gap: 1rem;
`;

const DownloadButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #0a819f;
  color: #ffffff;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
`;

const SliderWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  font-weight: 700;

  .SliderRoot {
    position: relative;
    display: flex;
    align-items: center;
    user-select: none;
    touch-action: none;
    width: 100%;
    height: 1.5rem;
    max-width: 50rem;
  }

  .SliderTrack {
    background-color: #fafafa;
    border: 1px solid #cccccc;
    position: relative;
    flex-grow: 1;
    height: 0.5625rem;
    border-radius: 1.25rem;
  }

  .SliderRange {
    position: absolute;
    background-color: #0a819f;
    border-radius: 1.25rem 0 0 1.25rem;
    height: 100%;
  }

  .SliderThumb {
    display: block;
    width: 1.4375rem;
    height: 1.4375rem;
    background-color: #0a819f;
    border-radius: 50%;
  }
`;

const IntroContainer = styled.div`
  max-width: 50rem;
`;

const OrganizationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-block-start: 1.5rem;

  label {
    font-size: 1.125rem;
  }
`;

const OrganizationValue = styled.span`
  font-weight: 700;
`;

const options = {
  focusTarget: 'Tip',
  tooltip: { isHtml: true },
};

const DONATION_HEADERS = [
  'Organization',
  'Legislator',
  'Amount ($)',
  { type: 'string', role: 'tooltip', p: { html: true } },
];

const stripHtmlTags = (str) => {
  if (!str) return '';
  return str.replace(/<\/?[^>]+(>|$)/g, '');
};

const convertToCSV = (data) => {
  if (!data || !data.length) return '';
  return data
    .map((row) =>
      row
        .map((item) => {
          // Handle tooltip object specially
          if (typeof item === 'object') return '';
          return typeof item === 'string' ? `"${stripHtmlTags(item)}"` : item;
        })
        .join(',')
    )
    .join('\n');
};

export default function SliderSankey({
  donatingOrganizations,
  totalIndex,
  billName,
}) {
  const donatingOrgs = useMemo(() => {
    const orgData = donatingOrganizations?.map((item) => [
      item.name,
      item.last,
      Number(item.donations),
      item.name +
        ' total donations to ' +
        item.last +
        ': <strong>$' +
        item.donations +
        '</strong> [' +
        item.contribution_time_period +
        ']',
    ]);

    return orgData ? [DONATION_HEADERS, ...orgData] : null;
  }, [donatingOrganizations]);

  // Memoize initial data processing
  const { initialKeyTotalMap, initialSortedKeys } = useMemo(() => {
    if (!donatingOrgs?.length) {
      return { initialKeyTotalMap: {}, initialSortedKeys: [] };
    }

    // Create map of 0th index properties
    const newMap = donatingOrgs.slice(1).reduce((acc, element) => {
      const key = element[0];
      acc[key] = key in acc ? [...acc[key], element] : [element];
      return acc;
    }, {});

    // Calculate totals in single pass
    const totalsArray = Object.entries(newMap).map(([key, values]) => [
      key,
      values.reduce((acc, curr) => acc + curr[totalIndex], 0),
    ]);

    // Sort once
    const sortedKeys = totalsArray
      .sort((a, b) => b[1] - a[1])
      .map(([key]) => key);

    return {
      initialKeyTotalMap: newMap,
      initialSortedKeys: sortedKeys,
    };
  }, [donatingOrgs, totalIndex]);

  const [keyTotalMap] = useState(initialKeyTotalMap);
  const [sortedKeys] = useState(initialSortedKeys);
  const [sliderValue, setSliderValue] = useState(() => [
    Math.min(3, initialSortedKeys.length),
  ]);

  // Memoize filtered data calculation
  const filteredData = useMemo(() => {
    if (!keyTotalMap || !sortedKeys.length) return donatingOrgs;

    const selectedKeys = sortedKeys.slice(0, sliderValue);
    const headers = [donatingOrgs[0]];
    const filteredRows = selectedKeys.flatMap((key) => keyTotalMap[key] || []);

    return [...headers, ...filteredRows];
  }, [donatingOrgs, keyTotalMap, sortedKeys, sliderValue]);

  // Memoize slider handler
  const handleSliderChange = useCallback((event) => {
    setSliderValue(event);
  }, []);

  const handleDownload = (billName) => {
    const csv = convertToCSV(filteredData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${billName} Filtered Organization Sankey.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleAllDownload = (billName) => {
    const csv = convertToCSV(donatingOrgs);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${billName} All Organization Sankey.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (!donatingOrgs?.length) return null;

  const chartHeight = sortedKeys.length < 19 ? "600px" : `${sortedKeys.length * 32}px`;

  return (
    <>
      <IntroContainer>
        <p>
          This display is intended to quickly show how much money has been given
          to each member of this committee (right) by organizations (left) that
          have taken a position on this bill. The size of the bars is
          proportionate to the total amount of direct contributions to the
          legislator and the exact amounts are revealed by hovering on each
          link. The data is based on available records between 2015 and 2024.
        </p>
        <OrganizationWrapper>
          <label htmlFor="num-organizations">
            Number of {donatingOrgs[0][0]}s:{' '}
            <OrganizationValue>{sliderValue}</OrganizationValue>
          </label>
          <SliderWrapper>
            <span>1</span>
            <Slider.Root
              type="range"
              id="num-organizations"
              name="num-organizations"
              className="SliderRoot"
              value={sliderValue}
              max={sortedKeys.length}
              step={1}
              onValueChange={handleSliderChange}
              min={1}
            >
              <Slider.Track className="SliderTrack">
                <Slider.Range className="SliderRange" />
              </Slider.Track>
              <Slider.Thumb className="SliderThumb" aria-label="Volume" />
            </Slider.Root>
            <span>{sortedKeys.length}</span>
          </SliderWrapper>
          <small>Shows the top N organizations by total amount given.</small>
        </OrganizationWrapper>
      </IntroContainer>
      <DownloadWrapper>
        <DownloadButton onClick={() => handleDownload(billName)}>
          Download Filtered Data
        </DownloadButton>
        <DownloadButton onClick={() => handleAllDownload(billName)}>
          Download All Data
        </DownloadButton>
      </DownloadWrapper>
      <Chart
        chartType="Sankey"
        width="100%"
        height={chartHeight}
        data={filteredData}
        options={options}
      />
    </>
  );
}
